import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from '@material-ui/core';
import { RootState } from '../reducers';
import { AuthUserState } from '../reducers/authUser';
import { getRealUser, isAuthenticated, isRealUser } from '../common/auth';
import { config } from '../config';
import { MIGRATION_BANNER_HEIGHT } from './MigrationBanner';

// Users still legitimately running both systems get unlimited access.
const UNLIMITED_ACCESS_EMAILS = [
    'filip.theodorakis@swisschemgas.com',
    'ggiannoulopoulos@swissmarine.gr',
    'ontokatzis@swisschemgas.com',
];

// Everyone else can view gated pages for VIEW_SECONDS, then must wait
// COOLDOWN_SECONDS before the next glimpse. The cycle is persisted in
// localStorage so refreshing the page does not grant extra time.
const VIEW_SECONDS = 30;
const COOLDOWN_SECONDS = 60;

interface GlimpseCycle {
    viewUntil: number;
    cooldownUntil: number;
}

const storageKey = (email: string) => `swisstech-timed-view-${email}`;

const readCycle = (key: string): GlimpseCycle | null => {
    try {
        const raw = window.localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (typeof parsed.viewUntil !== 'number' || typeof parsed.cooldownUntil !== 'number') return null;
        return parsed as GlimpseCycle;
    } catch (err) {
        return null;
    }
};

const startNewCycle = (key: string): GlimpseCycle => {
    const now = Date.now();
    const cycle: GlimpseCycle = {
        viewUntil: now + VIEW_SECONDS * 1000,
        cooldownUntil: now + (VIEW_SECONDS + COOLDOWN_SECONDS) * 1000,
    };
    try {
        window.localStorage.setItem(key, JSON.stringify(cycle));
    } catch (err) {
        // Ignore storage errors (e.g. private browsing) - worst case the timer resets on refresh
    }
    return cycle;
};

const loadOrStartCycle = (key: string): GlimpseCycle => {
    const existing = readCycle(key);
    if (existing !== null && Date.now() < existing.cooldownUntil) return existing;
    return startNewCycle(key);
};

const formatSeconds = (totalSeconds: number): string => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
};

const TimerChip = (props: { secondsLeft: number }) => {
    const urgent = props.secondsLeft <= 10;
    return (
        <div
            style={{
                position: 'fixed',
                top: MIGRATION_BANNER_HEIGHT + 8,
                right: 16,
                zIndex: 1301,
                padding: '6px 14px',
                borderRadius: 16,
                backgroundColor: urgent ? '#b71c1c' : 'rgba(0, 0, 0, 0.75)',
                color: '#fff',
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: 0.3,
            }}>
            Preview ends in {formatSeconds(props.secondsLeft)} &mdash; use BluHive
        </div>
    );
};

const BlockedPage = (props: { cooldownLeft: number }) => {
    return (
        <div
            style={{
                minHeight: '80vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: '0 24px',
            }}>
            <h1 style={{ marginBottom: 8 }}>Your preview time is up</h1>
            <p style={{ fontSize: 18, maxWidth: 640, marginTop: 0 }}>
                This page has moved to BluHive. SwissTech now only offers {VIEW_SECONDS}-second previews. Please do your work in BluHive.
            </p>
            <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
                <Button color="primary" variant="contained" href={config.BLUHIVE_URL} target="_blank" rel="noopener noreferrer">
                    Go to BluHive
                </Button>
                {config.BLUHIVE_PNL_URL && (
                    <Button variant="outlined" href={config.BLUHIVE_PNL_URL} target="_blank" rel="noopener noreferrer">
                        BluHive P&amp;L
                    </Button>
                )}
            </div>
            <p style={{ color: '#666' }}>
                Next {VIEW_SECONDS}-second preview available in {formatSeconds(props.cooldownLeft)}
            </p>
        </div>
    );
};

const GatedView = (props: { gateKey: string; children: React.ReactNode }) => {
    const [cycle, setCycle] = useState<GlimpseCycle>(() => loadOrStartCycle(props.gateKey));
    const [now, setNow] = useState<number>(() => Date.now());

    useEffect(() => {
        const timer = window.setInterval(() => setNow(Date.now()), 500);
        return () => window.clearInterval(timer);
    }, []);

    useEffect(() => {
        if (now >= cycle.cooldownUntil) setCycle(startNewCycle(props.gateKey));
    }, [now, cycle, props.gateKey]);

    if (now < cycle.viewUntil) {
        const secondsLeft = Math.max(0, Math.ceil((cycle.viewUntil - now) / 1000));
        return (
            <>
                <TimerChip secondsLeft={secondsLeft} />
                {props.children}
            </>
        );
    }

    const cooldownLeft = Math.max(0, Math.ceil((cycle.cooldownUntil - now) / 1000));
    return <BlockedPage cooldownLeft={cooldownLeft} />;
};

const TimedViewGate = (props: { children: React.ReactNode }) => {
    const authUser = useSelector<RootState, AuthUserState>((state) => state.authUser);
    // Only gate real (human) users; bots (e.g. the screenshot bot) are never gated
    if (!isAuthenticated(authUser) || !isRealUser(authUser)) return <>{props.children}</>;
    const email = getRealUser(authUser).email;
    if (UNLIMITED_ACCESS_EMAILS.includes(email)) return <>{props.children}</>;
    return <GatedView gateKey={storageKey(email)}>{props.children}</GatedView>;
};

export default TimedViewGate;
